import { AreaDisplay, DistrictDisplay, DivisionDisplay, TMClubDataPoint } from "@/dashboard";

/**
 * Club officer information, as `clubService.getClubByShortName` maps it out of the
 * `GetClubInfo` query.
 *
 * The schema declares *two* officer types and this mirrors the one the club-info payload
 * carries, `ClubInfoOfficer` — not the `ClubOfficer` it used to be named after, which is the
 * officer-management type and has `roleDisplayName` and a nested `user` instead of `email`,
 * `name` and `membershipId` (#3217). Against `ClubInfoOfficer` it is still a view rather than
 * the same type: `mapOfficer` folds every `null` to `undefined`, converts `userId` from the
 * wire's `Long` to a `number`, and builds a plain object with no `__typename`.
 */
export interface ClubInfoOfficerView {
  role: string;
  name: string;
  /** Null for a viewer the officer's email is masked from (#2193). */
  email: string | null;
  termBegin: string; // LocalDate as ISO string
  termEnd: string; // LocalDate as ISO string
  userId?: number; // User ID for officer management
  membershipId?: string; // Membership ID for officer management
}

/**
 * Club calendar configuration
 */
interface ClubCalendarConfig {
  state: string;
  overrideURL?: string;
  lastUpdate?: string;
}

/**
 * Club calendar meeting information
 */
interface ClubCalendarMeeting {
  id: string;
  startDate: string;
  description: string;
}

/**
 * Club calendar information
 */
interface ClubCalendar {
  isEnabled: boolean;
  config: ClubCalendarConfig;
  futureMeetings: ClubCalendarMeeting[];
  nextMeeting?: ClubCalendarMeeting;
  meetings: ClubCalendarMeeting[];
}

/**
 * Club guestbook configuration
 */
interface ClubGuestbookConfig {
  enabled: boolean;
  firstEmailBody?: string;
  reminderEmailBody?: string;
}

/**
 * Club email configuration
 */
interface EmailConfiguration {
  enabled: boolean;
  emailAddress: string;
  emailPassword: string;
  imapServer: string;
  smtpServer: string;
  smtpPort: number;
}

/**
 * Club membership status
 */
interface ClubMembershipStatus {
  enabled: boolean;
  newMemberRegistrationEnabled: boolean;
  cardPaymentsEnabled: boolean;
}

/**
 * Club image metadata, as `WebsiteConfig.vue` maps it out of the `GetClubImages` query.
 *
 * The schema declares `ClubImageMeta` with the same three field names, and this is not it
 * (#3217): the gallery builds plain objects with no `__typename`, and `size` is a `Long` on the
 * wire — `any` once codegen has mapped the scalar — where this narrows it to `number`.
 */
export interface ClubImageMetaView {
  imageName: string;
  uploadDate: string;
  size: number;
}

/**
 * Response from the images API endpoint
 */

/**
 * Active club member for officer assignment
 */

/**
 * Request to assign officer role
 */

/**
 * Meeting pattern representing a recurring meeting
 */
export interface MeetingPattern {
  dayOfWeek: string; // e.g., "MONDAY", "TUESDAY", etc.
  startTime: string; // HH:MM format, e.g., "19:00"
  durationHours: number; // e.g., 2.5 for 2.5 hours
}

/**
 * Club meeting schedule configuration
 */
export interface ClubMeetingSchedule {
  timezone: string; // e.g., "Europe/London"
  meetingPatterns: [number[], MeetingPattern][]; // Array of [weekNumbers, pattern] tuples from backend
  exclusions: string[]; // Array of ISO date strings to exclude
}

export const DEFAULT_MEMBER_SPEECH_REQUEST_ADVANCE_DAYS = 42;

/**
 * The client's working copy of a club's meeting configuration.
 *
 * The schema declares `ClubMeetingConfig` with the same five fields and the same types, and
 * this is still not it (#3217): every one of these is constructed by hand as a plain object —
 * `DEFAULT_CLUB_MEETING_CONFIG` below, the settings form's working copy in `ClubSettings.vue`,
 * `clubService`'s mapper and the meeting store's fallback — and the generated declaration
 * requires a `__typename` none of them has.
 */
export interface ClubMeetingConfigView {
  autoSyncAgendaToVoting: boolean;
  autoAddAgendaToFeedback: boolean;
  evaluatorVotingRoles: string[];
  allowMemberRoleSelfAssign: boolean;
  memberSpeechRequestAdvanceDays: number;
}

export const DEFAULT_CLUB_MEETING_CONFIG: ClubMeetingConfigView = {
  autoSyncAgendaToVoting: false,
  autoAddAgendaToFeedback: false,
  evaluatorVotingRoles: ["Evaluator", "Table Topics Evaluator"],
  allowMemberRoleSelfAssign: false,
  memberSpeechRequestAdvanceDays: DEFAULT_MEMBER_SPEECH_REQUEST_ADVANCE_DAYS,
};

/**
 * Club web configuration
 */
interface ClubWebConfig {
  clubId: { v: number };
  hostingOption: string;
  domainName?: string;
  clubUrl?: string;
  introductionText: string;
  tagLine: string;
  metaDesc?: string;
  logoImage?: string;
  backgroundImage?: string;
  locationShortDesc?: string;
  mapLink?: string;
  meetingTimes?: string;
  facebookLink?: string;
  twitterLink?: string;
  instagramLink?: string;
  meetupLink?: string;
  linkedInLink?: string;
  youtubeLink?: string;
  eventbriteLink?: string;
  websiteImages: Record<string, ClubImageMetaView>;
}

/**
 * Club website information
 */
interface ClubWebsite {
  webConfig: ClubWebConfig;
  clubWebsite?: string;
  getClubWebsite: () => string | undefined;
}

/**
 * Club guestbook information
 */
interface ClubGuestbook {
  isEnabled: boolean;
  firstEmailBody: string;
  reminderEmailBody: string;
}

/**
 * Club public information
 */
interface ClubPublicInfoDTO {
  clubId: number;
  shortName: string;
  longName: string;
  contactEmail: string;
  district: number;
  division: string;
  area: number;
  website: ClubWebsite;
  guestbookConfig: ClubGuestbookConfig;
  calendar: ClubCalendar;
  memberCount?: number;
  membershipStatus?: ClubMembershipStatus;
  clubOfficers: ClubInfoOfficerView[];
  meetingSchedule?: ClubMeetingSchedule;
  meetingConfig?: ClubMeetingConfigView;
  /** The club's TI lifecycle status, and the date TI suspended it where one was
   * recorded. `suspendDate` is null on an active or prospective club and on a
   * suspended one TI supplied no date for. */
  tiStatus: ClubLifecycleStatus;
  suspendDate: string | null;
}

/**
 * Per-club controls over whether the club's registered inbox is copied on
 * classes of automated notification (issue #1960).
 */
export interface ClubNotificationPreferences {
  copyInboxOnMeetingRole: boolean;
  copyInboxOnMembership: boolean;
  copyInboxOnGuest: boolean;
}

/**
 * Club manager information
 */
interface ClubManagerInfoDTO {
  easyspeakId?: number;
  guestbook: ClubGuestbook;
  periodicEmail: boolean;
  notificationPreferences: ClubNotificationPreferences;
  guestCount: number;
  memberCount: number;
  membershipStatus: ClubMembershipStatus;
  stripeConnected: boolean;
  clubOfficers: ClubInfoOfficerView[];
  emailConfiguration?: EmailConfiguration;
}

/**
 * Club DCP (Distinguished Club Program) data
 */
export interface ClubDCPData {
  oldCCs: number;
  oldCCsAdd: number;
  oldACs: number;
  oldACsAdd: number;
  oldLeaders: number;
  oldLeadersAdd: number;
  p1Level1s: number;
  p2Level2s: number;
  p3Level2sAdd: number;
  p4Level3s: number;
  p5Level45D: number;
  p6Level45DAdd: number;
  officerListOnTime: boolean;
  goal10Met: boolean;
  officersTrainedRd1: number;
  officersTrainedRd2: number;
  cotMet: boolean;
  memDuesOnTimeApr: boolean;
  memDuesOnTimeOct: boolean;
  newMembers: number;
  addNewMembers: number;
}

/**
 * A club, as `clubService.getClubByShortName` hands it to the rest of the app.
 *
 * The schema declares `ClubInfoResponse` with the same six field names, and this is not it
 * (#3217): the service rebuilds the whole tree by hand, so `publicInfo` and `managerInfo` are
 * the view models above rather than `ClubInfoPublic` / `ClubInfoManager`, and `dcpData` is a
 * parsed `TMClubDataPoint` where the wire carries the JSON string it was parsed from.
 */
export interface ClubInfoView {
  clubConnection: string;
  isOnToastHost: boolean;
  publicInfo: ClubPublicInfoDTO;
  managerInfo?: ClubManagerInfoDTO;
  dcpData?: TMClubDataPoint;
  clubMeetingsId: number;
}

/**
 * Club hierarchy data structures for Find Club feature
 */
export interface ClubEntry {
  /** TI club number — the stable navigation key when a hosted shortName is absent. */
  clubNumber: number;
  /** Canonical hosted short name; null for performance-only clubs with no model record. */
  clubShortName: string | null;
  clubLongName: string;
  isActive: boolean;
  isSuspended: boolean;
  tiStatus: ClubLifecycleStatus;
  /** ISO date TI suspended the club, where one was recorded. Null on an active or
   * prospective club, and on a suspended one TI supplied no date for. */
  suspendDate: string | null;
}

export interface AreaEntry {
  areaId: AreaDisplay;
  clubs: ClubEntry[];
}

export interface DivisionEntry {
  divisionId: DivisionDisplay;
  areas: AreaEntry[];
}

export interface DistrictEntry {
  districtId: DistrictDisplay;
  divisions: DivisionEntry[];
}

/** A club's Toastmasters International lifecycle status (distinct from its
 * DCP/performance status). Mirrors the backend `ClubTIStatus.label`. */
export type ClubLifecycleStatus = "Active" | "Suspended" | "Prospective";

/**
 * TI Club search result from the worldwide club directory
 */
export interface TIClubSearchResult {
  clubNumber: number;
  clubName: string;
  district: string;
  division: string;
  area: string;
  city: string | null;
  country: string | null;
  meetingDay: string | null;
  isOnToastHost: boolean;
  isProspective: boolean;
  clubShortName: string | null;
}
